import dayjs from 'dayjs'
import { formatShortDate } from '../../../ts-utils/date-utils'
import type { BalanceRoster } from '../../../services/teamBalance'
import type { TeamPlayerFinderRegistration } from '../../team/playerFinder/types'
import type { Roster } from '../../../types/Roster'

/**
 * One entry of the Team page Registration tab's Season picker.
 *
 * A team roster IS the team in one season (its root schedule), and every
 * Registration sub-tab is already roster-keyed — Team Budget by balance
 * discovery, Billing by the team's rosters, Player Finder by each registration's
 * `roster_id` — so a season is identified by its roster id.
 *
 * Field names follow the roster shape the shared roster picker renders
 * (`schedule_name`, the date range, `organization_name`).
 */
export interface RegistrationSeason {
  id: string
  schedule_name: string
  starts_at: string | null
  ends_at: string | null
  /**
   * The root schedule's uid, which `?sn_schedule=` links name: from the team's rosters
   * (Billing) or the Team Budget's discovery (SN-8467), the one source a rep usually has.
   */
  schedule_uid: string | null
  organization_name: string | null
}

export interface RegistrationSeasonSources {
  teamRosters: readonly Roster[]
  balanceRosters: readonly BalanceRoster[]
  finderRegistrations: readonly TeamPlayerFinderRegistration[]
}

const fromTeamRoster = (roster: Roster): RegistrationSeason => ({
  id: roster.id,
  schedule_name: roster.schedule_name || roster.name || '',
  starts_at: roster.starts_at || null,
  ends_at: roster.ends_at || null,
  schedule_uid: roster.schedule_uid || null,
  organization_name: roster.organization_name || null,
})

const fromBalanceRoster = (roster: BalanceRoster): RegistrationSeason => ({
  id: roster.id,
  schedule_name: roster.schedule_name || roster.name || '',
  starts_at: roster.starts_at,
  ends_at: roster.ends_at,
  // SN-8467 — an older api2 sends none, and the link then opens the default season.
  schedule_uid: roster.schedule_uid ?? null,
  organization_name: null,
})

const fromFinderRegistration = (
  registration: TeamPlayerFinderRegistration & { roster_id: string }
): RegistrationSeason => ({
  id: registration.roster_id,
  schedule_name: registration.competition?.name ?? '',
  starts_at: registration.competition?.starts_at ?? null,
  ends_at: registration.competition?.ends_at ?? null,
  schedule_uid: null,
  organization_name: null,
})

const hasRoster = (
  registration: TeamPlayerFinderRegistration
): registration is TeamPlayerFinderRegistration & { roster_id: string } =>
  Boolean(registration.roster_id)

/** Most recent first; a season with no dates (an unlinked roster) sorts last. */
const byStartDescending = (a: RegistrationSeason, b: RegistrationSeason) => {
  if (!a.starts_at || !b.starts_at) {
    return a.starts_at ? -1 : b.starts_at ? 1 : 0
  }

  return dayjs(b.starts_at).valueOf() - dayjs(a.starts_at).valueOf()
}

/**
 * The Season picker's options: every season ANY visible sub-tab has, once each.
 *
 * Sources are listed richest first, and the first source to name a season wins —
 * the team's rosters carry the schedule uid and organization, discovery the root
 * schedule's name and uid, and a Player Finder registration only its competition.
 * A registration with no roster cannot be scoped to a season, so it adds none.
 */
export const buildRegistrationSeasons = ({
  teamRosters,
  balanceRosters,
  finderRegistrations,
}: RegistrationSeasonSources): RegistrationSeason[] => {
  const seasons = new Map<string, RegistrationSeason>()
  const add = (season: RegistrationSeason) => {
    if (!seasons.has(season.id)) {
      seasons.set(season.id, season)
    }
  }

  teamRosters.forEach((roster) => add(fromTeamRoster(roster)))
  balanceRosters.forEach((roster) => add(fromBalanceRoster(roster)))
  finderRegistrations
    .filter(hasRoster)
    .forEach((registration) => add(fromFinderRegistration(registration)))

  return [...seasons.values()].sort(byStartDescending)
}

/**
 * The season ids one sub-tab has, in ITS source's order — the order its own
 * roster picker used, so its first entry is the season it defaulted to.
 */
export const finderSeasonIds = (
  registrations: readonly TeamPlayerFinderRegistration[]
): string[] => [
  ...new Set(
    registrations
      .filter(hasRoster)
      .map((registration) => registration.roster_id)
  ),
]

export interface RegistrationSeasonDeepLink {
  /** `?roster=` — the Team Budget notification link. */
  rosterId: string | null
  /** `?sn_schedule=` — the schedule uid the Billing roster picker wrote. */
  scheduleUid: string | null
}

/** The season a `?roster=` or `?sn_schedule=` link asks for, if any. */
export const registrationSeasonDeepLinkFromSearch = (
  search: string
): RegistrationSeasonDeepLink => {
  const params = new URLSearchParams(search)

  return {
    rosterId: params.get('roster') || null,
    scheduleUid: params.get('sn_schedule') || null,
  }
}

/**
 * The address-bar params a season PICK writes, so a reload reopens it.
 *
 * `sn_schedule` is the roster's schedule uid — the same value the Billing
 * roster picker wrote, so every existing reader keeps working. A season only
 * another source knows has none, so the param is cleared rather than left on a
 * stale season. `roster` (a notification link) is cleared too: it outranks
 * `sn_schedule` on resolve, so leaving it would reopen the link's season over
 * the one the user just picked. Null removes a param.
 */
export const registrationSeasonSearchParams = (
  season: Pick<RegistrationSeason, 'schedule_uid'> | undefined
): { sn_schedule: string | null; roster: null } => ({
  sn_schedule: season?.schedule_uid ?? null,
  roster: null,
})

export interface ResolveRegistrationSeasonArgs {
  seasons: readonly RegistrationSeason[]
  /** The season the user picked (or carried across a sub-tab switch). */
  userChoice: string | null
  deepLink: RegistrationSeasonDeepLink
  /**
   * The active sub-tab's own seasons, in its source's order. Absent a pick, the
   * season is the one that sub-tab defaulted to before the picker was shared.
   */
  activeSubTabSeasonIds: readonly string[]
}

/**
 * The season every sub-tab is scoped to: the user's pick, then a deep link, then
 * the active sub-tab's own default, then the newest season. A pick or link that
 * is not (or no longer) one of the seasons falls through, so a stale id can never
 * scope a sub-tab to a roster the caller cannot see.
 */
export const resolveRegistrationSeason = ({
  seasons,
  userChoice,
  deepLink,
  activeSubTabSeasonIds,
}: ResolveRegistrationSeasonArgs): string | null => {
  const isSeason = (id: string | null | undefined): id is string =>
    Boolean(id) && seasons.some((season) => season.id === id)

  if (isSeason(userChoice)) {
    return userChoice
  }
  if (isSeason(deepLink.rosterId)) {
    return deepLink.rosterId
  }
  const linkedSchedule = deepLink.scheduleUid
    ? seasons.find((season) => season.schedule_uid === deepLink.scheduleUid)
    : undefined
  if (linkedSchedule) {
    return linkedSchedule.id
  }
  const activeDefault = activeSubTabSeasonIds.find(isSeason)
  if (activeDefault) {
    return activeDefault
  }

  return seasons[0]?.id ?? null
}

/**
 * The Season picker's second line: the season's dates, prefixed by the
 * organization when known — "Canlan • Sep 24, 2026 - Oct 31, 2026". The same
 * line the roster picker shows, so a season reads the same on both tabs.
 *
 * The dates are calendar dates, read as written (not shifted through UTC), so a
 * viewer east of UTC never sees the season start a day early. An undated season
 * is an unlinked roster.
 */
export const registrationSeasonSubtitle = (
  season: Pick<
    RegistrationSeason,
    'starts_at' | 'ends_at' | 'organization_name'
  >
): string => {
  const range = season.starts_at
    ? [season.starts_at, season.ends_at]
        .filter((date): date is string => Boolean(date))
        .map((date) => formatShortDate(date, false))
        .join(' - ')
    : 'Unlinked Roster'

  return season.organization_name
    ? `${season.organization_name} • ${range}`
    : range
}
